/**
 * Reads a script's fields from its source text, for folder projects: the
 * browser version's editor never runs the game's code (only Play scene
 * does), so it reads the `Script.with({...})` call instead. Only literal values and the
 * `f` helpers are understood; anything else (a constant, a spread, a
 * computed value) makes the script's fields unreadable rather than guessed.
 * Nothing in the file is ever run.
 */
import type { FieldDef } from '@animo-editor/runtime';

export interface SourceFields {
  /** The class name, if it has one. */
  name?: string;
  fields: Record<string, FieldDef>;
  /** The object type's class name, such as Sprite, if the script names one. */
  objectType?: string;
}

class ParseError extends Error {}

type Token = { kind: 'punct' | 'ident' | 'number' | 'string'; value: string };

function tokenize(src: string, start: number): Token[] {
  const out: Token[] = [];
  let i = start;
  let depth = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) i++;
    else if (src.startsWith('//', i)) i = src.indexOf('\n', i) < 0 ? src.length : src.indexOf('\n', i);
    else if (src.startsWith('/*', i)) {
      const end = src.indexOf('*/', i + 2);
      if (end < 0) throw new ParseError('unclosed comment');
      i = end + 2;
    } else if (c === '"' || c === "'" || c === '`') {
      let j = i + 1;
      let text = '';
      while (j < src.length && src[j] !== c) {
        if (src[j] === '\\') {
          const n = src[j + 1];
          text += n === 'n' ? '\n' : n === 't' ? '\t' : (n ?? '');
          j += 2;
        } else {
          if (c === '`' && src.startsWith('${', j)) throw new ParseError('template expression');
          text += src[j++];
        }
      }
      if (j >= src.length) throw new ParseError('unclosed string');
      out.push({ kind: 'string', value: text });
      i = j + 1;
    } else if (/[0-9.]/.test(c) && (c !== '.' || /[0-9]/.test(src[i + 1] ?? ''))) {
      const m = /^(0[xX][0-9a-fA-F_]+|0[bB][01_]+|0[oO][0-7_]+|(?:[0-9][0-9_]*)?\.?[0-9_]*(?:[eE][+-]?[0-9]+)?)/.exec(src.slice(i))!;
      out.push({ kind: 'number', value: m[0] });
      i += m[0].length;
    } else if (/[A-Za-z_$]/.test(c)) {
      const m = /^[A-Za-z_$][A-Za-z0-9_$]*/.exec(src.slice(i))!;
      out.push({ kind: 'ident', value: m[0] });
      i += m[0].length;
    } else {
      out.push({ kind: 'punct', value: c });
      if ('([{'.includes(c)) depth++;
      if (')]}'.includes(c) && --depth < 0) break;
      i++;
    }
  }
  return out;
}

type Options = Record<string, unknown>;

/**
 * The runtime's `f` helpers, as plain functions. The runtime's own module
 * needs Phaser, which the service worker that shares this code can't load.
 * Keep in step with `f` in packages/runtime/src/script.ts.
 */
const helpers: Record<string, (...args: unknown[]) => FieldDef> = {
  float: (value = 0, options = {}) => ({ type: 'float', default: value, ...(options as Options) }),
  int: (value = 0, options = {}) => ({ type: 'int', default: value, ...(options as Options) }),
  string: (value = '', options = {}) => ({ type: 'string', default: value, ...(options as Options) }),
  bool: (value = false, options = {}) => ({ type: 'bool', default: value, ...(options as Options) }),
  color: (value = 0xffffff, options = {}) => ({ type: 'color', default: value, ...(options as Options) }),
  enum: (options, value = (options as unknown[])[0], rest = {}) => ({ type: 'enum', options, default: value, ...(rest as Options) }) as FieldDef,
  file: (options = {}) => ({ type: 'file', ...(options as Options) }),
  object: (options = {}) => ({ type: 'object', ...(options as Options) }),
  shape: (options = {}) => ({ type: 'shape', ...(options as Options) }),
  group: (fields, options = {}) => ({ type: 'class', fields, ...(options as Options) }) as FieldDef,
};

class Parser {
  private i = 0;
  constructor(private tokens: Token[]) {}

  peek(value?: string): Token | undefined {
    const t = this.tokens[this.i];
    return value === undefined || t?.value === value ? t : undefined;
  }

  next(value?: string): Token {
    const t = this.tokens[this.i++];
    if (!t || (value !== undefined && t.value !== value)) throw new ParseError(`expected ${value ?? 'more'}`);
    return t;
  }

  /** A run of `a.b.c`. */
  dotted(): string[] {
    const parts = [this.next().value];
    while (this.peek('.')) {
      this.next('.');
      parts.push(this.next().value);
    }
    return parts;
  }

  value(): unknown {
    const t = this.peek();
    if (!t) throw new ParseError('expected a value');
    if (t.value === '-' || t.value === '+') {
      this.next();
      const n = this.value();
      if (typeof n !== 'number') throw new ParseError('sign on a non-number');
      return t.value === '-' ? -n : n;
    }
    if (t.value === '{') return this.object();
    if (t.value === '[') return this.array();
    if (t.kind === 'string') return this.next().value;
    if (t.kind === 'number') {
      const text = this.next().value.replace(/_/g, '');
      const n = Number(text);
      if (Number.isNaN(n)) throw new ParseError('bad number');
      return n;
    }
    if (t.kind === 'ident') {
      if (t.value === 'true' || t.value === 'false') return this.next().value === 'true';
      if (t.value === 'null' || t.value === 'undefined') {
        this.next();
        return t.value === 'null' ? null : undefined;
      }
      const name = this.dotted();
      if (name.length !== 2 || name[0] !== 'f' || !Object.hasOwn(helpers, name[1]!)) throw new ParseError(`${name.join('.')} is not an f helper`);
      this.next('(');
      const args: unknown[] = [];
      while (!this.peek(')')) {
        args.push(this.value());
        if (!this.peek(')')) this.next(',');
      }
      this.next(')');
      this.asConst();
      return helpers[name[1]!]!(...args);
    }
    throw new ParseError(`unexpected ${t.value}`);
  }

  /** Skips a trailing `as const`. */
  asConst(): void {
    if (this.peek('as')) {
      this.next('as');
      this.next('const');
    }
  }

  object(): Record<string, unknown> {
    this.next('{');
    const out: Record<string, unknown> = {};
    while (!this.peek('}')) {
      const key = this.next();
      if (key.kind !== 'ident' && key.kind !== 'string' && key.kind !== 'number') throw new ParseError('computed key');
      this.next(':');
      out[key.value] = this.value();
      if (!this.peek('}')) this.next(',');
    }
    this.next('}');
    this.asConst();
    return out;
  }

  array(): unknown[] {
    this.next('[');
    const out: unknown[] = [];
    while (!this.peek(']')) {
      out.push(this.value());
      if (!this.peek(']')) this.next(',');
    }
    this.next(']');
    this.asConst();
    return out;
  }
}

/** The fields a script's source declares, or an error saying why they can't be read without running it. */
export function readScriptFields(src: string): SourceFields | { error: string } {
  const name = /export\s+default\s+class\s+([A-Za-z_$][\w$]*)/.exec(src)?.[1];
  const call = /\bScript\s*\.\s*with\s*(?:<[^(]*>)?\s*\(/.exec(src);
  // A script that extends Script directly has no fields.
  if (!call) return { ...(name && { name }), fields: {} };
  try {
    const p = new Parser(tokenize(src, call.index + call[0].length));
    if (p.peek(')')) return { ...(name && { name }), fields: {} };
    const fields = p.value();
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) throw new ParseError('fields are not an object');
    let objectType: string | undefined;
    if (p.peek(',')) {
      p.next(',');
      if (!p.peek(')')) objectType = p.dotted().at(-1);
    }
    return { ...(name && { name }), fields: fields as Record<string, FieldDef>, ...(objectType && { objectType }) };
  } catch (e) {
    if (!(e instanceof ParseError)) throw e;
    return { error: `its fields can't be read in the browser (${e.message}). Use literal values and f helpers in Script.with.` };
  }
}
